<script setup>
import {useUserGroupsStore, useUsersStore} from '@/stores'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useDesignSystem } from '@/composables/useDesignSystem'
import {profileIdValidator, urlValidator} from '@validators'
import GroupApplicationDialog from '@core/components/application/GroupApplicationDialog.vue'

const emit = defineEmits([
  'update:modelValue',
  'submit',
  'update:isDialogVisible'
])

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('md')

const usersStore = useUsersStore()
let myUser = null;
({ myUser } = storeToRefs(usersStore))

// RM-427: the group's Application questions panel
const applicationDialogOpen = ref(false)

const userGroupsStore = useUserGroupsStore()
let adminEditingUserGroup = null
let fetching = null;
({ adminEditingUserGroup, fetching } = storeToRefs(userGroupsStore))

const isDialogVisible = ref(false)
const isConfirmDialogOpen = ref(false)
const addFreeSubscription = ref(false)
const loading = ref(false)

const form = ref()
const valid = ref(true)
const changesMade = ref(false)
const errors = ref({})

const choosingProfileImage = ref(false)

const clearErrors = () => {
  if (errors.value) {
    errors.value = {}
  }
}

const onFormSubmit = async () => {
  loading.value = true
  if(valid.value){
    let id = adminEditingUserGroup.value.id
    let saveErrors = await userGroupsStore.saveAdminEditingUserGroup(true)

    if(saveErrors){
      console.error('Save errors:', saveErrors)
      errors.value = Object.assign(errors.value, saveErrors)
    } else {
      changesMade.value = false
      if(addFreeSubscription.value){
        await userGroupsStore.adminEditUserGroupSubscription(id)
      }
      loading.value = false
      isDialogVisible.value = false
      emit('update:isDialogVisible', false)
    }
  } else {
    loading.value = false
  }
}

const origin = ref(window.location.origin)

// Watch only for reference changes (null -> object or object -> null), not deep property changes
// This prevents re-computation on every keystroke
watch(() => adminEditingUserGroup.value, (newValue, oldValue) => {
  isDialogVisible.value = (newValue != null)
  if (!oldValue && newValue) {
    changesMade.value = false
  }
})

// Track form changes via a separate shallow watcher that only triggers on actual edits
// Using JSON.stringify comparison is expensive, so we use input events instead
const markAsChanged = () => {
  changesMade.value = true
}

const requestClose = () => {
  if (changesMade.value) {
    isConfirmDialogOpen.value = true
  } else {
    userGroupsStore.cancelAdminEditUserGroup()
    isDialogVisible.value = false
  }
}

const handleMediaSelection = selectedMedia => {
  
  if(selectedMedia){
    //rips out any ?t=9287439832749 that may have been added to refresh the image after cropping
    const match = String(selectedMedia).match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/)
    if (match) {
      const mediaId = match[0]    
      adminEditingUserGroup.value.profileImageId = mediaId
      changesMade.value = true
      choosingProfileImage.value = false
    } else {
      console.error('No valid UUID found in selectedMedia:', selectedMedia)
      adminEditingUserGroup.value.profileImageId = String(selectedMedia)
      changesMade.value = true
      choosingProfileImage.value = false
    }
  } else{
    adminEditingUserGroup.value.profileImageId = null
    changesMade.value = true

    //choosingProfileImage.value = false
  }
}

const subscriptionItems = [
  {text: 'None', value: null},
  {text:'2 Months Free of Platinum', value:'individual-1cycle-2month'},
  {text:'3 Months Free of Platinum', value:'individual-1cycle-3month'},
  {text: 'Individual 1 cycle annual', value: 'individual-1cycle-annual'},
]

const hasPermission = permission => {
  return myUser.value?.userLevel === permission || myUser.value.primaryGroupPermission === permission
}
</script>

<template>
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmDialogOpen"
    confirmation-msg="Save changes?"
    confirm-btn-text="Yes"
    cancel-btn-text="No"
    @confirm="userGroupsStore.saveAdminEditingUserGroup(); isDialogVisible = false;"
    @cancel="userGroupsStore.cancelAdminEditUserGroup(); isDialogVisible = false;"
  />
  <VDialog
    v-if="adminEditingUserGroup"
    v-model="isDialogVisible"
    :width="dWidth"
    persistent
    fullscreen
  >
    <VToolbar
      dark
      color="primary"
      class="toolbar-header"
    >
      <VToolbarItems style="width: 58px" />
      <VSpacer />
      <VToolbarTitle>Profile & Settings</VToolbarTitle>
      <VSpacer />
      <VToolbarItems>
        <VBtn
          icon="$close"
          size="large"
          variant="text"
          @click="requestClose"
        >
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </VToolbarItems>
    </VToolbar>
    <VCard class="pa-sm-9 pa-2 main-content pb-16">
      <VCardText class="pt-0">
        <div class="flex flex-column align-center">
          <div
            class="avatar mb-8"
            :class="{'avatar-placeholder': !adminEditingUserGroup.profileImageId}"
            @click="choosingProfileImage = !choosingProfileImage"
          >
            <VImg
              v-if="adminEditingUserGroup.profileImageId"
              class="avatar-image"
              :src="mediaUrl(adminEditingUserGroup.profileImageId, 'small')"
              cover
              height="100"
            />
            <div
              v-else
              class="avatar-fallback flex flex-row align-center justify-center"
            >
              <span class="avatar-fallback-text">add logo</span>
            </div>
            <div class="avatar-camera-icon-holder">
              <VIcon class="avatar-camera-icon">
                mdi-plus
              </VIcon>
            </div>
          </div>
        </div>
        <!-- 👉 Form -->
        <VForm
          ref="form"
          v-model="valid"
          @submit.prevent="onFormSubmit"
        >
          <MediaChooserAndManager
            v-model:is-dialog-visible="choosingProfileImage"  
            title="Choose Logo or Image" 
            :preselected-media-ids="[adminEditingUserGroup?.profileImageId]" 
            :user-group="adminEditingUserGroup"
            @media-selected="handleMediaSelection"
          />
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="adminEditingUserGroup.name"
                label="Group/Firm Name"
                autocomplete="null"
                :disabled="!(hasPermission('admin') || hasPermission('owner'))"
                @update:model-value="markAsChanged"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="adminEditingUserGroup.groupUrl"
                label="Group URL"
                autocomplete="null"
                placeholder="https://triumphjets.com"
                hint="Enter the group's website URL"
                :rules="[urlValidator]"
                persistent-hint
                @update:model-value="markAsChanged"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="adminEditingUserGroup.categoryName"
                label="Category Name"
                autocomplete="null"
                placeholder="Private Aviation"
                hint="Enter the business category (e.g., Private Aviation, Finance, etc.)"
                persistent-hint
                @update:model-value="markAsChanged"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="adminEditingUserGroup.groupDescription"
                label="Group Description"
                autocomplete="null"
                placeholder="Enter a description of the group"
                hint="Maximum 250 characters"
                maxlength="250"
                counter
                rows="3"
                persistent-hint
                @update:model-value="markAsChanged"
              />
            </VCol>
            <VCol cols="12">
              <VCheckbox
                v-model="adminEditingUserGroup.showOnPartnersPage"
                label="Show on Partners Page"
                hint="Enable this to display this group on the /luxurypartners page"
                persistent-hint
                @update:model-value="markAsChanged"
              />
            </VCol>
            <VRadioGroup
              v-if="hasPermission('admin')"
              v-model="adminEditingUserGroup.type"
              class="ml-4"
              @update:model-value="markAsChanged"
            >
              <VRadio value="discount">
                <template #label>
                  <div>Discount</div>
                </template>
              </VRadio>
              <VRadio value="enterprise">
                <template #label>
                  <div>Enterprise</div>
                </template>
              </VRadio>
              <VRadio value="friends">
                <template #label>
                  <div>Friends</div>
                </template>
              </VRadio>
              <VRadio value="restaurant">
                <template #label>
                  <div>Restaurant</div>
                </template>
              </VRadio>
            </VRadioGroup>
            <template v-if="hasPermission('admin') && adminEditingUserGroup.type == 'enterprise'">
              <VCol cols="12">
                <VTextField
                  v-model="adminEditingUserGroup.billingFirstName"
                  label="Billing First Name"
                  autocomplete="null"
                  @update:model-value="markAsChanged"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-model="adminEditingUserGroup.billingLastName"
                  label="Billing Last Name"
                  autocomplete="null"
                  @update:model-value="markAsChanged"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-model="adminEditingUserGroup.billingEmail"
                  label="Billing Email"
                  autocomplete="null"
                  @update:model-value="markAsChanged"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-model="adminEditingUserGroup.billingPhone"
                  label="Billing Phone"
                  autocomplete="null"
                  @update:model-value="markAsChanged"
                />
              </VCol>
            </template>
            <VCol
              v-if="adminEditingUserGroup.type == 'discount'"
              cols="12"
            >
              <VTextField
                v-model="adminEditingUserGroup.membershipPriceIndividualAnnual"
                label="Discounted annual rate"
                autocomplete="null"
                type="number"
                hint="$ amount an individual group member pays for an annual membership"
                @update:model-value="markAsChanged"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="adminEditingUserGroup.groupIdentity"
                label="Branding"
                hint="How we should address your members. (e.g. 'members of XYZ company')"
                autocomplete="null"
                @update:model-value="markAsChanged"
              />
              <span v-if="adminEditingUserGroup.groupIdentity">Welcome message shown on your page: <i>{{ `Welcome ${adminEditingUserGroup.groupIdentity}!` }}</i></span>
            </VCol>
            <VCol
              v-if="adminEditingUserGroup.type == 'discount'"
              cols="12"
            >
              <VTextField
                v-if="hasPermission('admin')"
                v-model="adminEditingUserGroup.publicId"
                label="Join url id"
                autocomplete="null"
                @update:model-value="markAsChanged"
              />
              <div v-if="adminEditingUserGroup.publicId">
                <div>Activation Url:</div>
                <div>
                  <a
                    :href="`${origin}/${adminEditingUserGroup.publicId}/join`"
                    target="_blank"
                  >{{ `${origin}/${adminEditingUserGroup.publicId}/join` }}</a>
                </div>
              </div>

              <VTextField
                v-model="adminEditingUserGroup.joinMessage"
                class="mt-2"
                label="Join Message"
                hint="Message displayed at the top of /join page. Default shown if left blank."
                autocomplete="null"
                @update:model-value="markAsChanged"
              />
              <VTextField
                v-model="adminEditingUserGroup.joinMessage2"
                class="mt-2"
                label="Join Message 2"
                hint="Message displayed on second line of /join page (shown in parentheses). Default shown if left blank."
                autocomplete="null"
                @update:model-value="markAsChanged"
              />
            </VCol>
            <VCol
              v-if="adminEditingUserGroup.type == 'enterprise'"
              cols="12"
            >
              <VTextField
                v-model="adminEditingUserGroup.publicId"
                label="Private Page on RM"
                autocomplete="null"
                :disabled="!(hasPermission('admin') || hasPermission('owner'))"
                :rules="[profileIdValidator]"
                :error-messages="errors.publicId"
                required
                counter="40"
                maxlength="40"
                @input="clearErrors"
                @update:model-value="markAsChanged"
              />

              <div v-if="adminEditingUserGroup.publicId">
                <div v-if="changesMade">
                  <div class="text-center">
                    <b>Page Preview: </b><br>{{ `reserverm.com/${adminEditingUserGroup.publicId}/activate` }}
                  </div>
                  <div class="text-center text-caption">
                    (Save to activate!)
                  </div>
                </div>
                <div v-else>
                  <div class="text-center">
                    <b>Your Page: </b><br><a
                      :href="`${origin}/${adminEditingUserGroup.publicId}/activate`"
                      target="_blank"
                    >{{ `reserverm.com/${adminEditingUserGroup.publicId}/activate` }}</a>
                  </div>
                  <div class="text-caption text-center">
                    Please tell your members to join RM here. You’ll get notified once they do & be able to grant them access.
                  </div>
                </div>
              </div>

              <VTextField
                v-model="adminEditingUserGroup.joinMessage"
                class="mt-2"
                label="Join Message"
                :hint="`Personalized line shown on your page. Default if left blank: '${adminEditingUserGroup.name} Group Membership'`"
                autocomplete="null"
                @update:model-value="markAsChanged"
              />
            </VCol>
            <VCol
              v-if="adminEditingUserGroup.type == 'friends'"
              cols="12"
            >
              <VTextField
                v-model="adminEditingUserGroup.publicId"
                label="Private Page on RM"
                autocomplete="null"
                :disabled="!(hasPermission('admin') || hasPermission('owner'))"
                :rules="[profileIdValidator]"
                :error-messages="errors.publicId"
                required
                counter="40"
                maxlength="40"
                @input="clearErrors"
                @update:model-value="markAsChanged"
              />
              <div v-if="adminEditingUserGroup.publicId">
                <div v-if="changesMade">
                  <div class="text-center">
                    <b>Page Preview: </b><br>{{ `reserverm.com/${adminEditingUserGroup.publicId}/friends` }}
                  </div>
                  <div class="text-center text-caption">
                    (Save to activate!)
                  </div>
                </div>
                <div v-else>
                  <div class="text-center">
                    <b>Your Page: </b><br><a
                      :href="`${origin}/${adminEditingUserGroup.publicId}/friends`"
                      target="_blank"
                    >{{ `reserverm.com/${adminEditingUserGroup.publicId}/friends` }}</a>
                  </div>
                  <div class="text-caption text-center">
                    Tell your friends to join RM here.
                  </div>
                </div>
              </div>
            </VCol>
            <!--
              RM-319: restaurant and restaurant-group teams have no join link.
              Registering through a group link added the account to the team
              as 'pending', and 'pending' was enough to see the restaurant's
              bookings. Teams are managed from the restaurant's Team settings;
              everyone signs in at reserverm.com/restaurants.
            -->
            <VCol cols="12">
              <VTextField
                v-model="adminEditingUserGroup.membershipMessage"
                label="Your branding on Member's Profile Page"
                hint="i.e. Gifted by Acme Co."
                autocomplete="null"
                @update:model-value="markAsChanged"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="adminEditingUserGroup.personalizedWelcomeMessage"
                label="Personalized Welcome Message"
                autocomplete="null"
                placeholder="Enter a personalized welcome message for members"
                hint="This message will be displayed to members when they join or visit the group page"
                rows="3"
                persistent-hint
                @update:model-value="markAsChanged"
              />
            </VCol>
            <!-- RM-427: this group's application (inherits its path's; RM Admins only) -->
            <VCol
              v-if="myUser?.userLevel === 'admin' && adminEditingUserGroup._id && ['discount', 'enterprise'].includes(adminEditingUserGroup.type)"
              cols="12"
            >
              <VBtn
                variant="tonal"
                color="primary"
                prepend-icon="mdi-form-select"
                @click="applicationDialogOpen = true"
              >
                Application questions
              </VBtn>
              <div class="text-caption mt-1">
                What this group's page asks. Saved separately from the rest of this form.
              </div>
              <GroupApplicationDialog
                v-model="applicationDialogOpen"
                :group-id="String(adminEditingUserGroup._id)"
              />
            </VCol>
            <VCol
              v-if="hasPermission('admin')"
              cols="12"
            >
              <VCheckbox
                v-if="adminEditingUserGroup.type != 'discount'"
                v-model="adminEditingUserGroup.platinum"
                label="Enable Platinum"
                @update:model-value="markAsChanged"
              />
              <VCheckbox
                v-if="adminEditingUserGroup.type != 'discount'"
                v-model="adminEditingUserGroup.canGift"
                label="Members can gift trial reservations"
                @update:model-value="markAsChanged"
              />
              <VCheckbox
                v-if="adminEditingUserGroup.type != 'discount'"
                v-model="adminEditingUserGroup.setPromptToVerify"
                label="Prompt new members to get verified"
                @update:model-value="markAsChanged"
              />
              <VCheckbox
                v-model="adminEditingUserGroup.autoConnectMembers"
                label="Connect all members"
                hint="Auto-connect every member to each other (bi-directional follow, no notification). New members are connected on join; removed members are disconnected."
                persistent-hint
                @update:model-value="markAsChanged"
              />
              <VSelect
                v-if="adminEditingUserGroup.type == 'discount' || adminEditingUserGroup.type == 'friends' "
                v-model="adminEditingUserGroup.planIdToGive"
                class="mt-4"
                label="New members receive..."
                :items="subscriptionItems"
                item-title="text"
                item-value="value"
                :return-object="false"
                hint="Select a subscription to be automatically given to new signups"
                @update:model-value="markAsChanged"
              />
              <VCheckbox
                v-if="adminEditingUserGroup.isNew && adminEditingUserGroup.type == 'enterprise' "
                v-model="addFreeSubscription"
                label="Add subscription after save"
              />
            </VCol>
            <!-- 👉 Submit and Cancel -->
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                v-if="true"
                :disabled="fetching || !changesMade"
                :loading="loading"
                @click="form?.validate(); onFormSubmit() "
              >
                Save
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.v-selection-control-group {
    display: flex;
    flex-direction: unset;
  }

  .v-toolbar-title{
    flex: unset;
  }

  .toolbar-header{
    position: fixed;
    z-index: 1000;
  }

  .main-content{
    top: 60px;
    position: relative;
  }

  .avatar {
    cursor: pointer;
    position: relative;
  }

  .avatar-placeholder {
    cursor: pointer;
    position: relative;
    width: 6rem;
    height: 6rem;
    background: var(--ds-brand-primary-light);
    outline: thin solid rgba(69, 182, 169, 0.25);
    outline-offset: 0.25rem;
  }

  @media screen and (min-width: 670px) {
    .avatar-placeholder {
      width: 6rem;
      height: 6rem;
      outline-offset: 0.5rem;
      margin: 1rem 2rem 0;
    }

    .avatar:hover {
      filter: brightness(0.8);
    }
  }

  .avatar-fallback {
    width: 100%;
    height: 100%;
    font-size: 1.6rem;
  }
  @media screen and (min-width: 670px) {
    .avatar-fallback {
      font-size: 2rem;
    }
  }

  .avatar-image {
    width: 100%;
    height: 100%;
    //border-radius: 50%;
  }

  .avatar-camera-icon-holder {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: -6%;
    right: -6%;
    z-index: 10;
    padding: 0.4rem;
    background: var(--ds-surface-inset);
    border-radius: 50%;
  }

  .avatar-camera-icon {
    width: 1.2rem;
    height: 1.2rem;
    transform: translateY(1px);
  }

  .avatar-fallback-text {
    font-size: 0.5em;
    width: fit-content;
  }
</style>
